import html2canvas from 'html2canvas'

const HIGHCHARTS_TYPES = ['line', 'vertical-bar', 'horizontal-bar', 'pie-donut', 'stacked-area', 'scatter-plot']

const captureHighchartsImage = (chartRef) =>
  new Promise((resolve) => {
    const chart = chartRef?.current?.chart
    if (!chart) { resolve(null); return }
    try {
      const svg = chart.getSVG()
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()
      const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' })
      const url = URL.createObjectURL(svgBlob)
      img.onload = () => {
        canvas.width = img.naturalWidth || 800
        canvas.height = img.naturalHeight || 400
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.drawImage(img, 0, 0)
        URL.revokeObjectURL(url)
        resolve(canvas.toDataURL('image/png'))
      }
      img.onerror = () => { URL.revokeObjectURL(url); resolve(null) }
      img.src = url
    } catch (e) {
      resolve(null)
    }
  })

const captureElementImage = async (element) => {
  if (!element) return null
  try {
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: false,
      useCORS: true
    })
    return canvas.toDataURL('image/png')
  } catch (e) {
    return null
  }
}

const dataUrlToBlob = (dataUrl) => {
  const [header, data] = dataUrl.split(',')
  const mimeMatch = header.match(/:(.*?);/)
  if (!mimeMatch) return null
  const mime = mimeMatch[1]
  const binary = atob(data)
  const arr = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i)
  return new Blob([arr], { type: mime })
}

export const uploadChartSnapshots = async ({
  dashboardId,
  chartRefs,
  token,
  baseUrl
}) => {
  const formData = new FormData()

  let index = 0
  for (const [chartId, chartInfo] of Object.entries(chartRefs)) {
    let imageDataUrl = null

    if (HIGHCHARTS_TYPES.includes(chartInfo.type)) {
      imageDataUrl = await captureHighchartsImage(chartInfo.ref)
    } else {
      imageDataUrl = await captureElementImage(chartInfo.domRef?.current)
    }

    if (!imageDataUrl) continue

    const blob = dataUrlToBlob(imageDataUrl)
    if (!blob) continue
    formData.append(`snapshots[${index}][image]`, blob, `chart-${chartId}.png`)
    formData.append(`snapshots[${index}][chart_id]`, chartId)
    formData.append(`snapshots[${index}][grid_row_id]`, chartInfo.gridRowId || '')
    formData.append(`snapshots[${index}][grid_column_position]`, String(chartInfo.gridColumnPosition ?? 0))
    formData.append(`snapshots[${index}][chart_title]`, chartInfo.title || '')
    formData.append(`snapshots[${index}][chart_type]`, chartInfo.type || '')
    index++
  }

  if (index === 0) {
    console.warn('uploadChartSnapshots: no charts captured, skipping upload')
    return
  }

  console.log(`uploadChartSnapshots: uploading ${index} chart snapshot(s)`)
  const response = await fetch(`${baseUrl}/dashboards/${dashboardId}/bulk_update_chart_snapshots`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new Error(`Snapshot upload failed (${response.status}): ${body.error || 'unknown error'}`)
  }
}

export const triggerDashboardPdfExport = async ({ dashboardId, token, baseUrl }) => {
  const response = await fetch(`${baseUrl}/dashboards/${dashboardId}/pdf_export`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) throw new Error('PDF export failed')
  return response.json()
}
